import { useState } from "react";
import { Mail, MapPin, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import Reveal from "@/components/Reveal";

const CARD_ICONS = [Mail, MapPin];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function inputCls() {
  return "w-full rounded-xl border border-linec bg-white px-4 py-3.5 text-sm text-night placeholder:text-mutedx/60 outline-none transition-colors duration-200 focus:border-terra focus:ring-2 focus:ring-terra/20";
}

export default function Contatti() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    interest: t.contact.form.interestOptions[0],
    message: "",
  });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, {
        name: form.name,
        email: form.email,
        organization: form.organization || null,
        interest: form.interest,
        message: form.message,
        lang,
      });
      toast.success(t.contact.form.success, {
        description: `${form.name} · ${form.email}`,
      });
      setForm({
        name: "",
        email: "",
        organization: "",
        interest: t.contact.form.interestOptions[0],
        message: "",
      });
    } catch (err) {
      toast.error(t.contact.form.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <main>
      <section className="relative overflow-hidden bg-night" data-testid="contact-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1664886761593-e7f82d8fca82?q=80&w=2200&auto=format&fit=crop"
            alt=""
            className="img-warm h-full w-full object-cover opacity-35"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-night via-night/70 to-night/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-40 lg:px-10 lg:pb-28 lg:pt-48">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-gold" data-testid="contact-eyebrow">
              <span className="inline-block h-[2px] w-8 bg-terra" />
              {t.contact.eyebrow}
            </p>
            <h1 className="mt-6 max-w-3xl font-serif text-5xl font-medium leading-[1.08] tracking-tight text-sand sm:text-6xl" data-testid="contact-title">
              {t.contact.title}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-sand/80 md:text-lg" data-testid="contact-sub">
              {t.contact.sub}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-sand" data-testid="contact-section">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 py-24 lg:grid-cols-12 lg:px-10 lg:py-32">
          <div className="lg:col-span-5">
            <div className="grid gap-5 sm:grid-cols-2">
              {t.contact.cards.map((c, i) => {
                const Icon = CARD_ICONS[i % CARD_ICONS.length];
                const Inner = (
                  <>
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-terra/10 text-terra">
                      <Icon className="h-5 w-5" />
                    </span>
                    <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-mutedx">
                      {c.label}
                    </p>
                    <p className="mt-1.5 break-words text-sm font-bold text-night">
                      {c.value}
                    </p>
                  </>
                );
                return (
                  <Reveal key={i} delay={i * 0.08}>
                    {c.href ? (
                      <a
                        href={c.href}
                        target={c.href.startsWith("http") ? "_blank" : undefined}
                        rel="noopener noreferrer"
                        data-testid={`contact-card-${i + 1}`}
                        className="block h-full rounded-2xl border border-linec bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-terra"
                      >
                        {Inner}
                      </a>
                    ) : (
                      <div
                        data-testid={`contact-card-${i + 1}`}
                        className="h-full rounded-2xl border border-linec bg-white p-6"
                      >
                        {Inner}
                      </div>
                    )}
                  </Reveal>
                );
              })}
            </div>

            <Reveal delay={0.2}>
              <div className="mt-10 rounded-2xl bg-night p-8" data-testid="contact-languages-note">
                <p className="font-serif text-xl italic leading-relaxed text-sand">
                  IT · AR · FR · EN
                </p>
                <p className="mt-3 text-sm leading-relaxed text-sand/65">
                  {t.contact.sub}
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="rounded-2xl border border-linec bg-white p-7 shadow-[0_24px_60px_-30px_rgba(16,42,67,0.25)] md:p-10"
              >
                <h2 className="font-serif text-2xl font-medium text-night md:text-3xl">
                  {t.contact.form.title}
                </h2>
                <p className="mt-2 text-xs text-mutedx">
                  * {t.contact.form.required}
                </p>

                <div className="mt-8 grid gap-5 md:grid-cols-2">
                  <div>
                    <label htmlFor="cf-name" className="mb-2 block text-xs font-bold text-night">
                      {t.contact.form.name} *
                    </label>
                    <input
                      id="cf-name"
                      required
                      value={form.name}
                      onChange={set("name")}
                      placeholder={t.contact.form.namePlaceholder}
                      className={inputCls()}
                      data-testid="contact-form-input-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="cf-email" className="mb-2 block text-xs font-bold text-night">
                      {t.contact.form.email} *
                    </label>
                    <input
                      id="cf-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      placeholder={t.contact.form.emailPlaceholder}
                      className={inputCls()}
                      data-testid="contact-form-input-email"
                    />
                  </div>
                  <div>
                    <label htmlFor="cf-org" className="mb-2 block text-xs font-bold text-night">
                      {t.contact.form.org}
                    </label>
                    <input
                      id="cf-org"
                      value={form.organization}
                      onChange={set("organization")}
                      placeholder={t.contact.form.orgPlaceholder}
                      className={inputCls()}
                      data-testid="contact-form-input-organization"
                    />
                  </div>
                  <div>
                    <label htmlFor="cf-interest" className="mb-2 block text-xs font-bold text-night">
                      {t.contact.form.interest}
                    </label>
                    <select
                      id="cf-interest"
                      value={form.interest}
                      onChange={set("interest")}
                      className={inputCls()}
                      data-testid="contact-form-input-interest"
                    >
                      {t.contact.form.interestOptions.map((o, oi) => (
                        <option key={oi} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="cf-message" className="mb-2 block text-xs font-bold text-night">
                    {t.contact.form.message} *
                  </label>
                  <textarea
                    id="cf-message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={set("message")}
                    placeholder={t.contact.form.messagePlaceholder}
                    className={inputCls() + " resize-none"}
                    data-testid="contact-form-input-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-form-submit"
                  className="group mt-8 inline-flex items-center gap-2.5 rounded-full bg-terra px-8 py-4 text-sm font-bold text-sand transition-all duration-200 hover:-translate-y-0.5 hover:bg-terra-dark disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  {sending ? t.contact.form.sending : t.contact.form.submit}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
